import * as React from 'react';
import * as _ from 'lodash';
import { useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
  Logging,
  Contact,
  Notification,
  NotificationData,
  ResponseError,
  Customer,
  API,
  getUuidV4,
  getUuidFromLocationHeader,
  UnitedStatesIso3,
  validateEmail,
  validatePhone,
  validatePhoneExtension,
  UserInfo,
  PermissionGroup,
  UserContext,
  getCreatePermission,
  validatePostalCode,
  Footer,
  VertexLink,
} from '@vcd-ecm-ui/Common';
import {
  VtxButton,
  VtxCollapse,
  VtxCollapsePanel,
  VtxIcon,
  VtxTooltip,
} from '@vertexinc/vtx-ui-react-component-library';
import ContactsEditProps from './ContactsAddEditProps.interface';
import '../../customer-info.scss';
import ContactItem from '../Contact/ContactItem';
import { adjustErrorDescription } from '../Common/Common';

const maxEmailLength = 100;
const maxAddressLength = 100;

const ContactsEdit = ({
  gatewayEndPoint,
  customer,
  onContactAddEditCancel,
  isEdit,
}: ContactsEditProps): JSX.Element => {
  const createNewContact = (isPrimary: boolean): Contact => {
    // Create a New Contact
    const newContact: Contact = {
      uuid: getUuidV4(),
      name: null,
      address: null,
      address2: null,
      department: null,
      city: null,
      state: null,
      postalCode: null,
      country: null,
      phone: null,
      phoneExtension: null,
      email: null,
      fax: null,
      primaryInd: isPrimary,
      emailSubscribedInd: true,
    };

    return newContact;
  };

  // Ensure that a Primary is Selected
  const getContactsWithPrimary = (): Contact[] => {
    const contactsCopy: Contact[] = _.cloneDeep(customer.contacts ? customer.contacts : []);
    let primaryContactSelected = false;

    contactsCopy.forEach((contactCopy: Contact) => {
      if (contactCopy.primaryInd === true) {
        // Primary is not yet selected
        if (!primaryContactSelected) {
          primaryContactSelected = true;
        } else {
          // Disable the Primary Ind if the primary is already selected.
          // eslint-disable-next-line no-param-reassign
          contactCopy.primaryInd = false;
        }
      }
    });

    // Primary Contact is not selected.  Select the first Contact as the Primary if there is one
    if (!primaryContactSelected && contactsCopy.length) {
      contactsCopy[0].primaryInd = true;
    }

    return contactsCopy;
  };

  const newContact: Contact = createNewContact(true);

  const [contacts, setContacts] = React.useState(isEdit
    ? getContactsWithPrimary()
    : [newContact]);

  const dispatch = useDispatch();

  const [redirect, setRedirect] = React.useState(null);
  const [notification, setNotification] = React.useState(null);
  const [activePrimaryKey, setActivePrimaryKey] = React.useState(isEdit
    // Edit: If there is only one Contact, select it. Otherwise select nothing
    ? customer.contacts && customer.contacts.length === 1 ? customer.contacts[0].uuid : null
    // Add: Select the New Contact
    : newContact.uuid);

  const [activeAdditionalKey, setActiveAdditionalKey] = React.useState(null);

  // Cancel
  const onCancel = (): void => {
    // Cancel
    Logging.Debug('Cancel');

    // Cancel Add Edit
    onContactAddEditCancel();
  };

  // Make the API Call to Add the Customer
  const addCustomer = (customerUpdated: Customer) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Add the Customer
      const headers: Headers = await api.customer.addCustomerV41(customerUpdated);
      const newCustomerUuid: string = getUuidFromLocationHeader(headers);

      // Direct back to Customers
      setRedirect('/customers');
    } catch (error) {
      const responseError: ResponseError = adjustErrorDescription(error as ResponseError, customerUpdated.taxpayerDefinition);

      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Customer Save Error',
        description: `Unable to Save Customer '${customerUpdated.name}'.`,
        error: responseError,
        appendMessagesForStatusCodes: [400, 409],
        hideStatusCodes: [400, 409],
      };

      setNotification(notificationData);
    }
  };

  // Make the API Call to Edit the Customer
  const editCustomer = (customerUpdated: Customer) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Edit the Customer
      await api.customer.editCustomerV41(customerUpdated);

      // Direct back to Customer View
      setRedirect(`/customer/view/${customer.uuid}/1`);
    } catch (error) {
      const responseError: ResponseError = adjustErrorDescription(error as ResponseError, customerUpdated.taxpayerDefinition);

      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Customer Save Error',
        description: `Unable to Save Customer '${customerUpdated.name}'.`,
        error: responseError,
        appendMessagesForStatusCodes: [400, 409],
        hideStatusCodes: [400, 409],
      };

      setNotification(notificationData);
    }
  };

  // Get the Updated Customer Values
  const getCustomerValues = (): Customer => {
    const customerUpdated: Customer = _.cloneDeep(customer);

    const copyContacts: Contact[] = _.cloneDeep(contacts);

    copyContacts.forEach((contact: Contact) => {
      // Customer had Contacts Before
      if (customer.contacts) {
        // Clear the Uuid's of all the new Contacts (not found in Original Contact List)
        if (customer.contacts.filter((originalContact) => originalContact.uuid === contact.uuid).length === 0) {
          // eslint-disable-next-line no-param-reassign
          contact.uuid = null;
        }
      } else { // All Customer Contacts are newly added
        // eslint-disable-next-line no-param-reassign
        contact.uuid = null;
      }
    });

    customerUpdated.contacts = copyContacts;

    return customerUpdated;
  };

  // Save
  const onSave = (): void => {
    // Save
    Logging.Debug('Save');

    // Get the Updated Customer Values
    const customerUpdated: Customer = getCustomerValues();

    // Edit
    if (isEdit) {
      dispatch(editCustomer(customerUpdated));
    } else { // Add
      dispatch(addCustomer(customerUpdated));
    }
  };

  // Primary Contact Selected
  const onPrimaryContactSelected = (key: string): void => {
    Logging.Debug('Primary Contact Selected', key);
    setActivePrimaryKey(key);
  };

  // Additional Contact Selected
  const onAdditionalContactSelected = (key: string): void => {
    Logging.Debug('Additional Contact Selected', key);
    setActiveAdditionalKey(key);
  };

  // Get the Index of the Contact using the Key (Uuid)
  const getContactIndex = (key: string): number => {
    let index = 0;
    let contactIndex = -1;

    contacts.forEach((contact: Contact) => {
      if (contact.uuid === key) {
        contactIndex = index;
      }

      index += 1;
    });

    return contactIndex;
  };

  const onUpdateContact = (contact: Contact): void => {
    // If is Important that the Primary Ind of the Updated are not modified by this function.
    // Otherwise the useEffect hook in Contact.tsx will go into an endless loop.
    // Prevent the Primary Ind from changing if it cannot change.

    // Update
    Logging.Debug('Update Contact', contact);

    const currentPrimaryContacts: Contact[] = contacts.filter((x: Contact) => x.primaryInd === true);
    const currentPrimaryContactKey: string = currentPrimaryContacts && currentPrimaryContacts.length === 1 ? currentPrimaryContacts[0].uuid : null;

    // Update the Modified contact
    const contactsCopy: Contact[] = _.cloneDeep(contacts);
    let primaryContactReplaced = false;

    // A different Contact is selected as the Primary.  Clear all other Primary Contacts
    if (contact.primaryInd && (contact.uuid !== currentPrimaryContactKey)) {
      primaryContactReplaced = true;

      contactsCopy.forEach((contactCopy: Contact) => {
        // eslint-disable-next-line no-param-reassign
        contactCopy.primaryInd = false;
      });
    }

    // Replace the Updated Contact
    const index = getContactIndex(contact.uuid);
    contactsCopy[index] = _.cloneDeep(contact);

    let primaryContactSelected = false;

    contactsCopy.forEach((contactCopy: Contact) => {
      if (contactCopy.primaryInd === true) {
        primaryContactSelected = true;
      }
    });

    // Primary Contact is not selected.
    if (!primaryContactSelected && contactsCopy.length) {
      // Select the first Contact as the Primary that is not the updated Contact
      contactsCopy.forEach((contactCopy: Contact) => {
        if (!primaryContactSelected && (contactCopy.uuid !== contact.uuid)) {
          // eslint-disable-next-line no-param-reassign
          contactCopy.primaryInd = true;

          primaryContactSelected = true;
        }
      });

      // A Primary Contact was changed to be the Additional Contact
      setActivePrimaryKey(null);
      setActiveAdditionalKey(contact.uuid);
    }

    // An Additional Contact was changed to be the Primary Contact
    if (primaryContactReplaced) {
      setActivePrimaryKey(contact.uuid);
      setActiveAdditionalKey(null);
    }

    setContacts(contactsCopy);
  };

  const onRemoveContact = (contact: Contact): void => {
    // Remove the Contact
    Logging.Debug('Remove Contact', contact);

    // Update the Modified contact
    const contactsCopy: Contact[] = _.cloneDeep(contacts);

    // Remove the Contact
    const index = getContactIndex(contact.uuid);
    contactsCopy.splice(index, 1);

    let primaryContactSelected = false;

    contactsCopy.forEach((contactCopy: Contact) => {
      if (contactCopy.primaryInd === true) {
        primaryContactSelected = true;
      }
    });

    // Primary Contact is not selected.  Select the first Contact as the Primary
    if (!primaryContactSelected && contactsCopy.length) {
      contactsCopy[0].primaryInd = true;
    }

    if (contact.primaryInd) {
      setActivePrimaryKey(null);
    } else {
      setActiveAdditionalKey(null);
    }

    setContacts(contactsCopy);
  };

  // Validate the Contact Values
  const validateContactValues = (): boolean => {
    let validated = true;

    contacts.forEach((contact: Contact) => {
      // Validate Name
      if (!contact.name) {
        validated = false;
        Logging.Info('Invalid Name');
      }

      // Validate Email
      if (!validateEmail(contact.email)) {
        validated = false;
        Logging.Info('Invalid Email');
      }

      if (contact.email && (contact.email.length > maxEmailLength)) {
        validated = false;
        Logging.Info('Invalid Email');
      }

      if (contact.address && (contact.address.length > maxAddressLength)) {
        validated = false;
        Logging.Info('Invalid Address');
      }

      if (contact.address2 && (contact.address2.length > maxAddressLength)) {
        validated = false;
        Logging.Info('Invalid Address2');
      }

      // Validate Phone
      if (!validatePhone(contact.phone)) {
        validated = false;
        Logging.Info('Invalid Phone');
      }

      // Validate Fax
      if (!validatePhone(contact.fax)) {
        validated = false;
        Logging.Info('Invalid Fax');
      }

      // Validate Phone Extension
      if (!validatePhoneExtension(contact.phoneExtension)) {
        validated = false;
        Logging.Info('Invalid Phone Extension');
      }

      // Validate Postal Code
      if (!validatePostalCode(contact.postalCode, contact.country)) {
        validated = false;
        Logging.Info('Invalid Postal Code');
      }
    });

    return validated;
  };

  // Add a New Contact
  const onAddAnotherContact = (): void => {
    // Create a New Contact.  Make the Contact the Primary if it is the first Contact
    const contact: Contact = createNewContact(contacts.length === 0);

    Logging.Debug('Add Another Contact', contact);

    // Add a new Contact
    const contactsCopy: Contact[] = _.cloneDeep(contacts);
    contactsCopy.push(contact);

    if (contact.primaryInd) {
      setActivePrimaryKey(contact.uuid);
      setActiveAdditionalKey(null);
    } else {
      setActivePrimaryKey(null);
      setActiveAdditionalKey(contact.uuid);
    }

    setContacts(contactsCopy);
  };

  // Render Contact
  // eslint-disable-next-line arrow-body-style
  const renderContact = (type: string, contact: Contact, primaryCanChange: boolean): JSX.Element => {
    Logging.Debug('Render Contact', type, contact, primaryCanChange);
    // Render the Contact
    return (
      <VtxCollapsePanel
        header={contact.name || 'No Contact Name provided'}
        key={contact.uuid}
        showArrow
        className="contactsEditCollapsePanel"
        extra={<VtxTooltip title="Remove the Contact"><VtxIcon type="delete" onClick={(event) => onRemoveContact(contact)} /></VtxTooltip>}
      >
        <ContactItem contact={contact} onUpdateContact={onUpdateContact} primaryCanChange={primaryCanChange} />
      </VtxCollapsePanel>
    );
  };

  // Compare 2 Contacts by Name
  // eslint-disable-next-line arrow-body-style
  const compareContacts = (contact1: Contact, contact2: Contact): number => {
    return contact1.name && contact2.name ? contact1.name.localeCompare(contact2.name) : 0;
  };

  // Render Primary Contacts
  // eslint-disable-next-line arrow-body-style
  const renderPrimaryContacts = (): JSX.Element[] => {
    // If there is only one Contact, it it the Primary regardless if it is set as one
    const isSingle = contacts.length === 1;

    // eslint-disable-next-line arrow-body-style
    return contacts.filter((contact: Contact) => isSingle || (contact.primaryInd === true)).sort(compareContacts).map((contact: Contact) => {
      return renderContact('Primary', contact, !isSingle);
    });
  };

  // Render Additional Contacts
  // eslint-disable-next-line arrow-body-style
  const renderAdditionalContacts = (): JSX.Element[] => {
    // eslint-disable-next-line arrow-body-style
    return contacts.filter((contact: Contact) => contact.primaryInd === false).sort(compareContacts).map((contact: Contact) => {
      return renderContact('Additional', contact, true);
    });
  };

  // Close the Notification
  const onNotificationClose = (): void => {
    setNotification(null);
  };

  // Redirect If Needed
  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  // Button States
  const cancelVisible = true;
  const validateVisible = false;
  const saveVisible = true;
  const saveEnabled = validateContactValues();
  const multipleContacts = contacts.length > 1;

  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);

  // Get the Contacts View Permission
  const hasContactsCreatePermission: boolean = getCreatePermission(userContext, PermissionGroup.Contacts);

  Logging.Debug('Contact Add/Edit', isEdit, contacts, activePrimaryKey, activeAdditionalKey);

  return (
    <>
      <div className="customerInfoContentContainer customerInfoContactsContentContainer">
        <div className="customerTabsContainer">
          <div className="contactEditContainer">
            {hasContactsCreatePermission && (
              <div className="contactsEditAddAnotherContact">
                <VtxButton
                  id="addButton"
                  icon={<VtxIcon type="plus" />}
                  onClick={onAddAnotherContact}
                  type="primary"
                >
                  Add Another Contact
                </VtxButton>
              </div>
            )}
            {
              multipleContacts
              && (
                <div className="contactsEditPrimaryContactsHeader">
                  Primary Contact
                </div>
              )
            }
            {
              contacts.length > 0
              && (
                <div className="contactsEditPrimaryContacts">
                  <VtxCollapse
                    accordion
                    onChange={onPrimaryContactSelected}
                    className="contactsEditCollapse"
                    activeKey={activePrimaryKey}
                  >
                    {renderPrimaryContacts()}
                  </VtxCollapse>
                </div>
              )
            }
            {
              multipleContacts
              && (
                <>
                  <div className="contactsEditAdditionalContactsHeader">
                    Additional Contact(s)
                  </div>
                  <div className="contactsEditAdditionalContacts">
                    <VtxCollapse
                      accordion
                      onChange={onAdditionalContactSelected}
                      className="contactsEditCollapse"
                      activeKey={activeAdditionalKey}
                    >
                      {renderAdditionalContacts()}
                    </VtxCollapse>
                  </div>
                </>
              )
            }
          </div>
        </div>
      </div>
      <Footer
        validateVisible={validateVisible}
        onCancel={onCancel}
        cancelVisible={cancelVisible}
        saveVisible={saveVisible}
        saveEnabled={saveEnabled}
        onSave={onSave}
      />
      <Notification
        data={notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
    </>
  );
};

export default ContactsEdit;
